/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
import CloseIcon from '@mui/icons-material/Close';
import FastForwardIcon from '@mui/icons-material/FastForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import PackSizeText from 'common/components/PackSizeText';
import ReshippingIcon from 'common/components/ReshippingIcon';
import ShipByDateCell from 'common/components/ShipByDateCell';
import {
  DISABLED_TAGS_STATUSES,
  DISABLED_TRANSFER_OUT_STATUSES,
} from 'common/constants/orderStatusGroups';
import usePrintLabelContext, {
  PrintLabelCheckBox,
  SelectAllCheckBox,
} from 'common/hooks/usePrintLabelContext';
import useWebSocket from 'common/hooks/useWebSocket';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { EnableInventoryLookup, casingFormat, sortTags } from 'common/utils';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import lineItemEnum from 'enums/lineItem.enum';
import { orderSource } from 'enums/order.enum';
import {
  capitalize,
  find,
  first,
  includes,
  isBoolean,
  isEmpty,
  isNil,
  map,
  reduce,
  toLower,
  upperCase,
} from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import stc from 'string-to-color';

import colors from 'common/constants/colors';
import useRoles from 'common/hooks/useRoles';
import WorkQueues from 'enums/workQueue.enum';
import ManageTagsDialog from 'modules/PatientView/components/EligibilityActionItems/ManageTagsDialog';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import { BULK_LABEL_PAGE_SIZE } from 'api/requests';
import {
  applyUrgentTag,
  listBulkLabelsByPage,
  removeUrgentTag,
  sortBulkLabelsWorkQueue,
} from 'store/thunks/workQueueThunks';
import { useBulkQueueMode } from './ChangeBulkModeContext';
import {
  FacilityCheckBox,
  SelectAllFacilityCheckBox,
  useTransferFacilityContext,
} from './MultiTransferFacilityButton';
import ShipByDateOverrideDialog from './ShipByDateOverrideDialog';
import ShippingOptionChip from './ShippingOptionChip';
import TransferOutDialog from './TransferOutDialog';

const TagsPopoverContent = ({
  selectedTagsLineItem,
  loadingLineItems,
  formatTagText,
  handleRemoveTag,
  handleRemoveAllTags,
}) => {
  const isLoading = loadingLineItems.has(selectedTagsLineItem.lineItemId);
  // Only show user-managed tags in the edit popover — system tags are already visible in the row
  const tags = (selectedTagsLineItem.tags || []).filter((tag) => !tag.isSystemTag);

  return (
    <Box sx={{ p: 1.5, width: '400px', position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          minHeight: '56px',
          maxHeight: '200px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
          bgcolor: '#fff',
          '&:hover': {
            borderColor: '#1976d2',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            flex: 1,
            maxHeight: '180px',
            overflowY: 'auto',
            p: 1,
            pr: '40px',
            mr: '-23px',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          {tags.map((tag, index) => {
            const tagKey = tag?.tagName || '';
            const isSystemTag = tag?.isSystemTag;
            return (
              <Chip
                key={tagKey || index}
                label={formatTagText(tag)}
                size='small'
                onDelete={
                  !isSystemTag ? () => handleRemoveTag(selectedTagsLineItem, tag) : undefined
                }
                deleteIcon={!isSystemTag ? <CloseIcon /> : undefined}
                disabled={isLoading}
                sx={{
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                  },
                  ...(isSystemTag && { opacity: 0.7, cursor: 'default' }),
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            zIndex: 1,
          }}
        >
          <Tooltip title='Remove all tags' arrow placement='top'>
            <IconButton
              size='small'
              onClick={() => handleRemoveAllTags(selectedTagsLineItem)}
              disabled={isLoading}
              sx={{
                bgcolor: '#FFD54F',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: '#FFC107',
                },
                '&.Mui-disabled': {
                  bgcolor: '#F5F5F5',
                  opacity: 0.6,
                },
              }}
            >
              <CloseIcon
                sx={{
                  fontSize: '18px',
                  color: '#000',
                  fontWeight: 'bold',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress size={27} />
        </Box>
      )}
    </Box>
  );
};

const TagsTableCell = ({ lineItem, isDisabled, isLoading, formatTagText, handleTagsClick }) => {
  const allTags = (lineItem.tags || []).filter((tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY');
  const systemTags = allTags.filter((tag) => tag.isSystemTag);
  const userTags = allTags.filter((tag) => !tag.isSystemTag);

  if (allTags.length === 0) {
    return null;
  }

  const handleClick = (event) => {
    if (!isDisabled && !isLoading) {
      handleTagsClick(event, lineItem);
    }
  };

  const isInteractionDisabled = isDisabled || isLoading;
  const firstUserTag = userTags[0];
  const remainingUserTags = userTags.slice(1);

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
      {/* System tags — always fully visible, no interaction */}
      {systemTags.length > 0 && (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
          {systemTags.map((tag) => (
            <Chip
              key={tag.tagName}
              label={formatTagText(tag)}
              size='small'
              color={tag.tagName === 'URGENT' ? 'error' : 'default'}
              sx={{
                cursor: 'default',
                ...(tag.tagName !== 'URGENT' && { opacity: 0.7 }),
                ...(tag.tagName === 'URGENT' && { fontWeight: 600 }),
              }}
            />
          ))}
        </Box>
      )}

      {/* User tags — first tag clickable to edit, +N shows tooltip with remaining */}
      {userTags.length > 0 && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: isInteractionDisabled ? 'default' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <Tooltip title='Click to manage tags' arrow placement='top'>
            <Chip
              label={formatTagText(firstUserTag)}
              size='small'
              disabled={isLoading}
              onClick={handleClick}
              sx={{
                maxWidth: '120px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Tooltip>

          {remainingUserTags.length > 0 && (
            <Tooltip
              title={remainingUserTags.map((tag) => formatTagText(tag)).join(', ')}
              arrow
              placement='top'
            >
              <Chip
                label={`+${remainingUserTags.length}`}
                size='small'
                variant='outlined'
                disabled={isLoading}
                sx={{ minWidth: '40px', cursor: 'default' }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const BulkLabelsTable = ({ staleLineItems = new Set(), lockedInventoryItems = [] }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isTransferFacilityMode, isCentralFillMode } = useBulkQueueMode();
  const { setLabelItemIds, lockedByOtherUsers, lockedItemsUserMap } = usePrintLabelContext();
  const { clearTransferItems } = useTransferFacilityContext();
  const { isConnected, sendMessage } = useWebSocket();
  const { pages, currentPage, filter, loading, sortBy } = useSelector(
    ({ workQueue }) => workQueue.bulkLabels
  );
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);
  const bulkLabels = pages[currentPage];
  const hasAccess = useRoles();

  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const [isTransferOutDialogOpen, setIsTransferOutDialogOpen] = useState(false);
  const [isShipByDateDialogOpen, setIsShipByDateDialogOpen] = useState(false);
  const [shipByDateLineItemId, setShipByDateLineItemId] = useState(null);
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [selectedTagsLineItem, setSelectedTagsLineItem] = useState(null);
  const [loadingLineItems, setLoadingLineItems] = useState(new Set());
  const [urgentLoadingId, setUrgentLoadingId] = useState(null);

  const isReviewFilterActive = !!filter?.taggedForReview;
  const transferEligibleStates = [
    'PROCESSING',
    'ORDER_VERIFICATION',
    'ORDER_RE_VERIFICATION',
    'OPEN',
  ];

  const headCells = [
    {
      id: 'orderId',
      label: 'Order Id',
      align: 'left',
      sortable: true,
    },
    {
      id: 'orderDate',
      label: 'Order Date',
      align: 'left',
      sortable: true,
    },
    {
      id: 'itemCount',
      label: '# Items',
      align: 'left',
      sortable: true,
    },
    {
      id: 'patientName',
      label: 'Patient',
      align: 'left',
      sortable: true,
    },
    {
      id: 'state',
      label: 'State',
      align: 'left',
    },
    {
      id: 'medicationName',
      label: 'Drug Name',
      align: 'left',
      sortable: true,
    },
    // POM-5862: with the inventory lookup on, NDC becomes its own column, Type folds into the
    // Drug Name cell as an OTC chip, and Label Date / Facility drop out entirely (both remain
    // available as filters). While the switch is off the layout is the live master/prod one:
    // no NDC column, and Type / Label Date / Facility all present.
    ...(EnableInventoryLookup
      ? [
          {
            id: 'ndc',
            label: 'NDC',
            align: 'left',
            sortable: false,
          },
        ]
      : [
          {
            id: 'isOtc',
            align: 'left',
            sortable: false,
            label: 'Type',
          },
        ]),
    {
      id: 'quantity',
      label: 'Qty',
      align: 'left',
      sortable: true,
    },
    ...(EnableInventoryLookup
      ? []
      : [
          {
            id: 'createdAt',
            label: 'Label Date',
            align: 'left',
            sortable: true,
          },
        ]),
    {
      id: 'shipByDate',
      label: 'Ship By Date',
      align: 'left',
      sortable: true,
    },
    {
      id: 'partner',
      label: 'Partner',
      align: 'left',
      sortable: true,
    },
    {
      id: 'practice',
      label: 'Practice',
      align: 'left',
      sortable: true,
    },
    {
      id: 'shippingOption',
      label: 'Shipping',
      align: 'left',
      sortable: true,
    },
    {
      id: 'px',
      label: 'Px',
      align: 'left',
    },
    {
      id: 'source',
      label: 'Source',
      align: 'left',
      sortable: false,
    },
    ...(isReviewFilterActive
      ? [
          {
            id: 'status',
            label: 'Status',
            align: 'left',
          },
        ]
      : []),
    ...(EnableInventoryLookup
      ? []
      : [
          {
            id: 'Facility',
            label: 'Facility',
            align: 'left',
          },
        ]),
    {
      id: 'tags',
      label: 'Tags',
      align: 'left',
      sortable: false,
    },
    {
      id: 'lockedBy',
      label: '',
      align: 'left',
    },
  ];

  const lockedKeySet = new Set(
    map(lockedInventoryItems, ({ ndc, fulfillingPharmacyId }) => `${ndc}::${fulfillingPharmacyId}`)
  );

  // productId orders carry no NDC until fulfillment, so also key locked items by
  // productId::pharmacy. A drug is locked when ANY of its NDCs is under count, so any locked
  // NDC's productId locks every bulk-queue row for that product.
  const lockedProductKeySet = new Set(
    lockedInventoryItems
      .filter(({ productId }) => productId)
      .map(({ productId, fulfillingPharmacyId }) => `${productId}::${fulfillingPharmacyId}`)
  );

  // POM: productId backfill is complete, so a fulfillment-pending item with neither a productId
  // nor an already-bound NDC is unexpected (e.g. a create-time catalog association that silently
  // failed). Bulk fill would reject it server-side (PRODUCT_ID_UNRESOLVED / DEFAULT_NDC_UNRESOLVED)
  // — surface that here instead of letting the user find out after submitting.
  const isProductUnresolved = ({ lineItemStatus, productId, ndc }) =>
    EnableInventoryLookup &&
    lineItemStatus === lineItemEnum.PRODUCT_FULFILLMENT &&
    !productId &&
    !ndc;

  const allLabelItemIds = reduce(
    bulkLabels,
    (acc, lineItem) => {
      const {
        lineItemId,
        isTaggedForReview,
        lineItemLabelId,
        ndc,
        fulfillingPharmacyId,
        productId,
      } = lineItem;
      const isDisabled =
        // Central Fill may select tagged-for-review orders (see per-row checkbox).
        (!isCentralFillMode && isTaggedForReview) ||
        includes(lockedByOtherUsers, lineItemId) ||
        staleLineItems.has(lineItemId) ||
        !lineItemLabelId ||
        lockedKeySet.has(`${ndc}::${fulfillingPharmacyId}`) ||
        lockedProductKeySet.has(`${productId}::${fulfillingPharmacyId}`) ||
        isProductUnresolved(lineItem);

      return !isDisabled ? [...acc, lineItemId] : acc;
    },
    []
  );

  const allLineItems =
    bulkLabels
      ?.filter(
        ({ lineItemId, processingState, lineItemLabelId }) =>
          !!lineItemId &&
          transferEligibleStates.includes(processingState) &&
          !lockedItemsUserMap[lineItemId] &&
          !staleLineItems.has(lineItemId) &&
          !!lineItemLabelId
      )
      .map(({ lineItemId, orderId, medicationName, rxNumber, processingState }) => ({
        lineItemId,
        orderId,
        itemName: medicationName,
        rxNumber,
        processingState,
      })) || [];

  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions);

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.bulkLabels.count / BULK_LABEL_PAGE_SIZE)
  );

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortBulkLabelsWorkQueue({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didn't change, don't do anything.
      return;
    }

    if (isTransferFacilityMode) {
      clearTransferItems();
    } else if (isConnected && !isEmpty(bulkLabels)) {
      const lockedItemsInCurrentPage = bulkLabels
        .filter(({ lineItemId }) => !includes(lockedByOtherUsers, lineItemId))
        .map(({ lineItemId }) => lineItemId);

      if (!isEmpty(lockedItemsInCurrentPage)) {
        sendMessage('lineItems:unlock', {
          lineItemIds: lockedItemsInCurrentPage,
          sessionId: sessionStorage.getItem('socketSessionId'),
        });
      }
    }

    dispatch(listBulkLabelsByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  useEffect(() => {
    if (isTransferFacilityMode) {
      setLabelItemIds([]);
    } else {
      clearTransferItems();
    }
  }, [isTransferFacilityMode, setLabelItemIds, clearTransferItems]);

  const handleCopyLineItemId = (event, lineItemId) => {
    event.stopPropagation();
    navigator.clipboard.writeText(lineItemId);
  };

  const handleMenuOpen = (event, lineItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedLineItem(lineItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleManageTagsClick = () => {
    handleMenuClose();
    setIsTagsDialogOpen(true);
  };

  const handleTransferOutClick = () => {
    handleMenuClose();
    setIsTransferOutDialogOpen(true);
  };

  const handleShipByDateDialogClose = () => {
    setIsShipByDateDialogOpen(false);
    setShipByDateLineItemId(null);
  };

  const handleTagsDialogClose = () => {
    setIsTagsDialogOpen(false);
    setSelectedLineItem(null);
  };

  const handleTransferOutDialogClose = () => {
    setIsTransferOutDialogOpen(false);
    setSelectedLineItem(null);
  };

  const handleTransferOutSuccess = () => {
    handleTransferOutDialogClose();
    dispatch(listBulkLabelsByPage({ page: currentPage }));
  };

  const handleSaveTags = async ({ lineItemId, tags: newTags, urgentShipByDate }) => {
    const currentTags = selectedLineItem.tags || [];
    const currentTagNames = currentTags.map((t) => t.tagName);
    const newTagNames = (newTags || []).map((t) => t.tagName);
    const urgentAdded = newTagNames.includes('URGENT') && !currentTagNames.includes('URGENT');
    const urgentRemoved = !newTagNames.includes('URGENT') && currentTagNames.includes('URGENT');
    const urgentReAdded =
      Boolean(urgentShipByDate) &&
      newTagNames.includes('URGENT') &&
      currentTagNames.includes('URGENT');

    if (!urgentAdded && !urgentRemoved && !urgentReAdded) {
      return dispatch(upsertLineItemTags({ lineItemId, currentTags, newTags })).then((response) => {
        const updatedTags = first(response?.updatedLineItemTags)?.tags;
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.BULK_LABELS,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        handleTagsDialogClose();
        return response;
      });
    }

    // URGENT tag was added, removed, or re-added with a new ship by date
    let urgentResult;
    if (urgentAdded || urgentReAdded) {
      urgentResult = await dispatch(
        applyUrgentTag({
          lineItemId,
          mpi: selectedLineItem.mpi,
          orderId: selectedLineItem.orderId,
          shipByDate: urgentShipByDate,
        })
      );
    } else {
      urgentResult = await dispatch(
        removeUrgentTag({
          lineItemId,
          mpi: selectedLineItem.mpi,
          orderId: selectedLineItem.orderId,
        })
      );
    }

    // Also handle any concurrent non-URGENT tag changes
    const nonUrgentCurrentTags = currentTags.filter((t) => t.tagName !== 'URGENT');
    const nonUrgentNewTags = (newTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentCurrentNames = nonUrgentCurrentTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    const nonUrgentNewNames = nonUrgentNewTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    let updatedTags = urgentResult?.tags;
    if (nonUrgentCurrentNames !== nonUrgentNewNames) {
      const tagResponse = await dispatch(
        upsertLineItemTags({
          lineItemId,
          currentTags: nonUrgentCurrentTags,
          newTags: nonUrgentNewTags,
        })
      );
      updatedTags = first(tagResponse?.updatedLineItemTags)?.tags ?? updatedTags;
    }

    dispatch(
      workQueueActions.updateLineItemTags({
        preset: WorkQueues.BULK_LABELS,
        page: currentPage,
        lineItemId,
        tags: updatedTags,
      })
    );
    handleTagsDialogClose();
    return { updatedLineItemTags: [{ lineItemId, tags: updatedTags }] };
  };

  const handleTagsClose = () => {
    setTagsAnchorEl(null);
    setSelectedTagsLineItem(null);
  };

  const handleRemoveTag = (lineItem, tagToRemove) => {
    const lineItemId = lineItem?.lineItemId;
    if (!lineItemId || loadingLineItems.has(lineItemId)) {
      return;
    }
    const currentTags = lineItem.tags || [];
    const tagToRemoveName = tagToRemove?.tagName || '';
    const updatedTags = currentTags.filter((tag) => tag.tagName !== tagToRemoveName);
    setLoadingLineItems((prev) => new Set(prev).add(lineItemId));

    const nonSystemTags = updatedTags.filter((tag) => !tag.isSystemTag);
    if (nonSystemTags.length === 0) {
      handleTagsClose();
    }

    dispatch(
      upsertLineItemTags({
        lineItemId,
        currentTags,
        newTags: updatedTags,
      })
    )
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.BULK_LABELS,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        if (nonSystemTags.length > 0) {
          setSelectedTagsLineItem((prev) => ({
            ...prev,
            tags: updatedTags,
          }));
        }
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItemId);
          return newSet;
        });
      });
  };

  const handleRemoveAllTags = (lineItem) => {
    setLoadingLineItems((prev) => new Set(prev).add(lineItem.lineItemId));
    handleTagsClose();

    // Preserve system tags — only remove user-managed tags
    const systemTagsToKeep = (lineItem.tags || []).filter((tag) => tag.isSystemTag);

    return dispatch(
      upsertLineItemTags({
        lineItemId: lineItem.lineItemId,
        currentTags: lineItem.tags || [],
        newTags: systemTagsToKeep,
      })
    )
      .then(() => {
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.BULK_LABELS,
            page: currentPage,
            lineItemId: lineItem.lineItemId,
            tags: systemTagsToKeep,
          })
        );
      })
      .finally(() => {
        setLoadingLineItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lineItem.lineItemId);
          return newSet;
        });
      });
  };

  const formatTagText = (tag) => {
    const tagName = tag?.tagName || '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleTagsClick = (event, lineItem) => {
    event.stopPropagation();
    setTagsAnchorEl(event.currentTarget);
    setSelectedTagsLineItem(lineItem);
  };

  return (
    <Grid container direction='column'>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Tooltip
          title={
            DISABLED_TAGS_STATUSES.includes(selectedLineItem?.processingState)
              ? `${selectedLineItem?.processingState} state is not eligible for managing tags`
              : ''
          }
          arrow
          placement='left'
          disableHoverListener={!DISABLED_TAGS_STATUSES.includes(selectedLineItem?.processingState)}
        >
          <span style={{ display: 'flex', width: '100%' }}>
            <MenuItem
              onClick={handleManageTagsClick}
              disabled={DISABLED_TAGS_STATUSES.includes(selectedLineItem?.processingState)}
              sx={{ width: '100%' }}
            >
              Manage Tags
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip
          title={
            DISABLED_TRANSFER_OUT_STATUSES.includes(selectedLineItem?.processingState)
              ? `${selectedLineItem?.processingState} state is not eligible for transfer out`
              : ''
          }
          arrow
          placement='left'
          disableHoverListener={
            !DISABLED_TRANSFER_OUT_STATUSES.includes(selectedLineItem?.processingState)
          }
        >
          <span style={{ display: 'flex', width: '100%' }}>
            <MenuItem
              onClick={handleTransferOutClick}
              disabled={DISABLED_TRANSFER_OUT_STATUSES.includes(selectedLineItem?.processingState)}
              sx={{ width: '100%' }}
            >
              Transfer Out
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {selectedLineItem && (
        <>
          <ManageTagsDialog
            open={isTagsDialogOpen}
            onClose={handleTagsDialogClose}
            mpi={selectedLineItem.mpi}
            orderId={selectedLineItem.orderId}
            lineItemId={selectedLineItem.lineItemId}
            existingTags={selectedLineItem.tags || []}
            onSave={handleSaveTags}
            patientDetails={{ patientName: selectedLineItem.patientName }}
          />
          <TransferOutDialog
            open={isTransferOutDialogOpen}
            onClose={handleTransferOutDialogClose}
            selectedLineItems={[selectedLineItem]}
            onSuccess={handleTransferOutSuccess}
          />
          <ShipByDateOverrideDialog
            open={isShipByDateDialogOpen}
            onClose={handleShipByDateDialogClose}
            showPatientBanner
            lineItem={(() => {
              const live =
                bulkLabels?.find((item) => item.lineItemId === shipByDateLineItemId) ||
                selectedLineItem;
              return {
                lineItemId: live.lineItemId,
                orderId: live.orderId,
                itemName: live.medicationName,
                shipByDate: live.shipByDate,
                needsByDate: live.needsByDate,
                tags: live.tags,
                mpi: live.mpi,
                rxNumber: live.rxNumber,
                patientName: selectedLineItem?.patientName,
              };
            })()}
          />
        </>
      )}

      <Popover
        open={Boolean(tagsAnchorEl)}
        anchorEl={tagsAnchorEl}
        onClose={handleTagsClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '400px',
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              mt: -1,
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: -5,
                left: 5,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '10px 10px 0 10px',
                borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
              },
            },
          },
        }}
      >
        {selectedTagsLineItem?.tags?.length > 0 && (
          <TagsPopoverContent
            selectedTagsLineItem={selectedTagsLineItem}
            loadingLineItems={loadingLineItems}
            formatTagText={formatTagText}
            handleRemoveTag={handleRemoveTag}
            handleRemoveAllTags={handleRemoveAllTags}
          />
        )}
      </Popover>

      {!loading && (
        <Grid item>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.align}
                      style={
                        headCell.id === 'fillType' ? { position: 'relative', right: '10px' } : {}
                      }
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={sortBy.sortKey === headCell.id}
                          direction={
                            sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                          }
                          data-head-property={headCell.id}
                          onClick={handleSort}
                        >
                          {headCell.label}
                          {sortBy.sortKey === headCell.id ? (
                            <Box component='span' sx={visuallyHidden}>
                              {toLower(sortBy.sortOrder) === 'desc'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell />
                  <TableCell align='right' sx={{ py: 0 }}>
                    {isTransferFacilityMode ? (
                      <SelectAllFacilityCheckBox
                        allLineItems={allLineItems}
                        isDisabled={isEmpty(allLineItems)}
                      />
                    ) : (
                      <SelectAllCheckBox
                        allLineItemIds={allLabelItemIds}
                        isDisabled={isEmpty(allLabelItemIds)}
                      />
                    )}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(bulkLabels, ({ mpi, patientName, ...lineItem }) => {
                  const fillingFacility = find(
                    facilities,
                    (facility) => facility.fulfillingPharmacyId === lineItem.fulfillingPharmacyId
                  );

                  const facilityName = fillingFacility?.name;

                  const rxPrefix = fillingFacility?.config?.rxPrefix;
                  const facilityState = fillingFacility?.state;

                  const facilityCheckBoxDisabled = !transferEligibleStates.includes(
                    lineItem.processingState
                  );

                  const isLockedByOtherUser = lockedItemsUserMap[lineItem.lineItemId];
                  const isStale = staleLineItems.has(lineItem.lineItemId);
                  const hasLabel = !!lineItem.lineItemLabelId;
                  const isLockedForCount =
                    lockedKeySet.has(`${lineItem.ndc}::${lineItem.fulfillingPharmacyId}`) ||
                    lockedProductKeySet.has(
                      `${lineItem.productId}::${lineItem.fulfillingPharmacyId}`
                    );
                  const isUnresolvedProduct = isProductUnresolved(lineItem);

                  const isDisabled = isLockedByOtherUser || isStale || !hasLabel;
                  const disabledColor = { color: isDisabled ? 'grey.500' : 'black' };

                  return (
                    <TableRow
                      key={lineItem.lineItemId}
                      sx={{
                        bgcolor: isDisabled ? 'grey.100' : 'inherit',
                        position: 'relative',
                      }}
                      aria-disabled={isDisabled}
                    >
                      <TableCell align='left' sx={disabledColor}>
                        {lineItem.orderId ? (
                          <Link
                            to={`/orders/${lineItem.orderId}`}
                            style={{ color: theme.palette.text.primary }}
                          >
                            {lineItem.orderId}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100, ...disabledColor }} align='left'>
                        {lineItem.orderDate
                          ? format(new Date(lineItem.orderDate), 'MM/dd/yyyy')
                          : 'N/A'}
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {lineItem.orderLineItemCount || 'N/A'}
                      </TableCell>
                      <TableCell component='th' scope='row' align='left'>
                        <Link
                          to={`/patients/${mpi}`}
                          style={{
                            color: isDisabled
                              ? theme.palette.text.disabled
                              : theme.palette.text.primary,
                          }}
                        >
                          {patientName}
                        </Link>
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {upperCase(lineItem.state || 'N/A')}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: 'inline-flex', alignItems: 'center', ...disabledColor }}
                        >
                          <Box component='span'>
                            {formatDrugLabel({ ...lineItem, drugName: lineItem.medicationName })}
                          </Box>
                          {EnableInventoryLookup && lineItem.isOtc && (
                            <Chip
                              size='small'
                              variant='outlined'
                              color='info'
                              label='OTC'
                              sx={{
                                ml: 0.5,
                                height: 14,
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 0.5, fontSize: '0.5rem' },
                              }}
                            />
                          )}
                          {lineItem.lineItemId && (
                            <Tooltip
                              title={`ItemId: ${lineItem.lineItemId}`}
                              arrow
                              placement='top'
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: lighten(theme.palette.text.primary, 0.9),
                                    color: theme.palette.text.primary,
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: lighten(theme.palette.text.primary, 0.9),
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size='small'
                                onClick={(e) => handleCopyLineItemId(e, lineItem.lineItemId)}
                                sx={{ ml: 0.5 }}
                              >
                                <ContentCopyIcon fontSize='inherit' />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isLockedForCount && (
                            <Tooltip
                              title='Drug is being counted for physical inventory. Fulfillment is blocked until the count is complete.'
                              arrow
                              placement='top'
                            >
                              <Chip
                                size='small'
                                icon={
                                  <FactCheckIcon
                                    sx={{
                                      fontSize: '0.85rem',
                                      color: `${colors.warningAmber} !important`,
                                    }}
                                  />
                                }
                                label='Counting'
                                sx={{
                                  ml: 0.5,
                                  height: 20,
                                  bgcolor: colors.warningAmberLight,
                                  border: `1px solid ${colors.warningAmberBorder}`,
                                  color: 'text.primary',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </Tooltip>
                          )}
                          {isUnresolvedProduct && (
                            <Tooltip
                              title="This item has no product ID or NDC on file, so it can't be resolved for fulfillment. Contact support to fix the product association."
                              arrow
                              placement='top'
                            >
                              <Chip
                                size='small'
                                icon={
                                  <WarningIcon
                                    sx={{
                                      fontSize: '0.85rem',
                                      color: `${theme.palette.error.main} !important`,
                                    }}
                                  />
                                }
                                label='Unresolved Product'
                                sx={{
                                  ml: 0.5,
                                  height: 20,
                                  bgcolor: theme.palette.error.light,
                                  border: `1px solid ${theme.palette.error.main}`,
                                  color: 'text.primary',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <PackSizeText
                          packSizeValue={lineItem.packSizeValue}
                          packSizeUnit={lineItem.packSizeUnit}
                          formAlias={lineItem.formAlias}
                          noMappingMessage='No mappings here'
                        />
                      </TableCell>
                      {/* An NDC is only bound at fulfillment, and OTC items are catalog SKUs that
                          never carry one — so an empty cell is the norm, not an error state. Render
                          a dash rather than leaving the cell blank or printing "undefined". */}
                      {EnableInventoryLookup && (
                        <TableCell align='left' sx={disabledColor}>
                          {lineItem.ndc || '-'}
                        </TableCell>
                      )}
                      {!EnableInventoryLookup && (
                        <TableCell align='left' sx={disabledColor}>
                          {isBoolean(lineItem.isOtc) && lineItem.isOtc ? 'OTC' : 'RX'}
                        </TableCell>
                      )}
                      <TableCell align='left' sx={disabledColor}>
                        {lineItem.quantity || 'N/A'}
                      </TableCell>
                      {!EnableInventoryLookup && (
                        <TableCell sx={{ width: 100, ...disabledColor }} align='left'>
                          {lineItem.createdAt
                            ? format(new Date(lineItem.createdAt), 'MM/dd/yyyy')
                            : 'N/A'}
                        </TableCell>
                      )}
                      <TableCell align='left' sx={disabledColor}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ShipByDateCell
                            shipByDate={lineItem.shipByDate}
                            tags={lineItem.tags}
                            onClick={
                              !['CANCELED', 'SHIPPED', 'SPLIT', 'RESHIPMENT'].includes(
                                lineItem.processingState
                              )
                                ? () => {
                                    setSelectedLineItem({ ...lineItem, mpi, patientName });
                                    setShipByDateLineItemId(lineItem.lineItemId);
                                    setIsShipByDateDialogOpen(true);
                                  }
                                : undefined
                            }
                          />
                          {!['CANCELED', 'SHIPPED', 'SPLIT', 'RESHIPMENT'].includes(
                            lineItem.processingState
                          ) &&
                            !(
                              find(lineItem.tags, { tagName: 'URGENT' }) &&
                              lineItem.shipByDate === format(new Date(), 'yyyy-MM-dd')
                            ) && (
                              <Tooltip title='Mark as Urgent Today' arrow>
                                <span>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    disabled={urgentLoadingId === lineItem.lineItemId}
                                    onClick={async () => {
                                      setUrgentLoadingId(lineItem.lineItemId);
                                      try {
                                        await dispatch(
                                          applyUrgentTag({
                                            lineItemId: lineItem.lineItemId,
                                            shipByDate: format(new Date(), 'yyyy-MM-dd'),
                                            mpi,
                                            orderId: lineItem.orderId,
                                          })
                                        );
                                        enqueueSnackbar('Urgent tag applied', {
                                          variant: 'success',
                                        });
                                      } catch {
                                        enqueueSnackbar('Failed to apply urgent tag', {
                                          variant: 'error',
                                        });
                                      } finally {
                                        setUrgentLoadingId(null);
                                      }
                                    }}
                                  >
                                    {urgentLoadingId === lineItem.lineItemId ? (
                                      <CircularProgress size={12} color='error' />
                                    ) : (
                                      <FastForwardIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {casingFormat(lineItem.partner) || 'N/A'}
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {casingFormat(lineItem.practice) || 'N/A'}
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {lineItem.shippingOption ? (
                          <ShippingOptionChip
                            optionCode={lineItem.shippingOption}
                            label={
                              find(
                                shippingOptions,
                                (option) => option.optionCode === lineItem.shippingOption
                              )?.shippingOption || lineItem.shippingOption
                            }
                          />
                        ) : (
                          'NA'
                        )}
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {rxPrefix ? (
                          <FacilityAvatar
                            rxPrefix={rxPrefix}
                            facilityName={facilityName}
                            facilityState={facilityState}
                          />
                        ) : (
                          <Grid />
                        )}
                      </TableCell>
                      <TableCell sx={disabledColor}>
                        <Tooltip
                          title={orderSource[lineItem.source]?.sourceValue || ''}
                          arrow
                          placement='top'
                        >
                          <span>{orderSource[lineItem.source]?.sourceKey || 'NA'}</span>
                        </Tooltip>
                      </TableCell>
                      {isReviewFilterActive && (
                        <TableCell sx={disabledColor}>
                          {lineItem.reviewReason
                            ? capitalize(lineItem.reviewReason.replace(/_/g, ' ').toLowerCase())
                            : 'NA'}
                        </TableCell>
                      )}
                      {!EnableInventoryLookup && (
                        <TableCell align='left' sx={disabledColor}>
                          {facilityName} - {facilityState}
                        </TableCell>
                      )}
                      <TableCell align='left' sx={{ ...disabledColor, position: 'relative' }}>
                        <TagsTableCell
                          lineItem={{
                            ...lineItem,
                            tags: lineItem.tags ? sortTags(lineItem.tags) : [],
                          }}
                          isDisabled={isDisabled}
                          isLoading={loadingLineItems.has(lineItem.lineItemId)}
                          formatTagText={formatTagText}
                          handleTagsClick={handleTagsClick}
                        />
                      </TableCell>
                      <TableCell sx={disabledColor}>
                        <Tooltip
                          title={
                            lineItem.inBatchShipment
                              ? batchShippingTooltip({
                                  isBusinessAddress: lineItem.isBusinessAddress,
                                  isClickable: hasAccess.viewBatchShipments,
                                })
                              : ''
                          }
                          arrow
                          placement='top'
                        >
                          {lineItem.inBatchShipment ? (
                            <BatchShippingIcon
                              onClick={
                                hasAccess.viewBatchShipments
                                  ? () => navigateToBatchShipmentQueue(lineItem.orderId)
                                  : undefined
                              }
                              color={perfectRxTheme.palette.warning.warningColour}
                              showBadge={lineItem.isBusinessAddress}
                            />
                          ) : (
                            <Grid>
                              {lineItem.isBusinessAddress && <BusinessAddressIndicator />}
                            </Grid>
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={disabledColor}>
                        {lineItem.isReshipment ? (
                          <Tooltip
                            title='Item being processed as a reshipment'
                            arrow
                            placement='top'
                          >
                            <span>
                              <ReshippingIcon color={perfectRxTheme.palette.info.main} />
                            </span>
                          </Tooltip>
                        ) : (
                          <Grid />
                        )}
                      </TableCell>
                      <TableCell align='right' sx={disabledColor}>
                        {isTransferFacilityMode ? (
                          <FacilityCheckBox
                            lineItem={{
                              lineItemId: lineItem.lineItemId,
                              orderId: lineItem.orderId,
                              itemName: lineItem.medicationName,
                              rxNumber: lineItem.rxNumber,
                              processingState: lineItem.processingState,
                            }}
                            isDisabled={facilityCheckBoxDisabled || isLockedByOtherUser}
                          />
                        ) : (
                          <PrintLabelCheckBox
                            lineItemId={lineItem.lineItemId}
                            // Central Fill may send tagged-for-review orders to
                            // compounding, so isTaggedForReview must not block
                            // selection in that mode (it still blocks label
                            // printing in Fulfillment mode).
                            isDisabled={
                              (!isCentralFillMode && lineItem.isTaggedForReview) ||
                              isStale ||
                              !hasLabel ||
                              isLockedForCount ||
                              isUnresolvedProduct
                            }
                            disabledReason={
                              // eslint-disable-next-line no-nested-ternary
                              isLockedForCount
                                ? 'Drug is being counted for physical inventory. Fulfillment is blocked until the count is complete.'
                                : isUnresolvedProduct
                                  ? "This item has no product ID or NDC on file, so it can't be resolved for fulfillment. Contact support to fix the product association."
                                  : undefined
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell align='left' sx={disabledColor}>
                        {(() => {
                          if (staleLineItems.has(lineItem.lineItemId)) {
                            return (
                              <Tooltip
                                title='This item has been updated. Please refresh the list.'
                                arrow
                                placement='top'
                              >
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <WarningIcon
                                    sx={{
                                      color: theme.palette.warning.main,
                                      width: 23,
                                      height: 23,
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            );
                          }

                          const lockedUser = lockedItemsUserMap[lineItem.lineItemId];

                          if (lockedUser) {
                            const userFirstName = lockedUser.firstName;
                            const userLastName = lockedUser.lastName;
                            const userFullName = `${userFirstName} ${userLastName}`;

                            return (
                              <Tooltip
                                title={`This item is being worked on by ${userFullName}`}
                                arrow
                                placement='top'
                              >
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Avatar
                                    sx={{
                                      bgcolor: stc(userFullName),
                                      color: theme.palette.getContrastText(stc(userFullName)),
                                      width: 23,
                                      height: 23,
                                      fontSize: '0.70rem',
                                      p: 2,
                                    }}
                                    alt={userFullName}
                                  >
                                    {userFirstName.charAt(0).toUpperCase()}
                                    {userLastName.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -6,
                                      right: -4,
                                      bgcolor: 'error.main',
                                      borderRadius: '50%',
                                      width: 16,
                                      height: 16,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <LockIcon sx={{ fontSize: '10px', color: 'white' }} />
                                  </Box>
                                </Box>
                              </Tooltip>
                            );
                          }

                          return <Grid />;
                        })()}
                      </TableCell>
                      {!hasLabel && (
                        <TableCell>
                          <Tooltip
                            title='Label not created'
                            arrow
                            placement='top'
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '20px',
                                },
                              },
                            }}
                          >
                            <InfoIcon />
                          </Tooltip>
                        </TableCell>
                      )}
                      {isTransferFacilityMode ? (
                        <TableCell />
                      ) : (
                        <TableCell align='left' sx={disabledColor}>
                          <IconButton
                            size='small'
                            onClick={(e) => handleMenuOpen(e, { ...lineItem, mpi, patientName })}
                            disabled={isDisabled}
                          >
                            <MoreVertIcon fontSize='small' />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>

              {!isNil(bulkLabels) && totalPages > 1 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={24} align='right'>
                      <Pagination
                        sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                        count={totalPages}
                        page={currentPage}
                        size='small'
                        onChange={handlePageChange}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Grid>
      )}

      {(isNil(bulkLabels) || loading) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(bulkLabels) && isEmpty(bulkLabels) && !loading && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

BulkLabelsTable.propTypes = {
  staleLineItems: PropTypes.instanceOf(Set),
  lockedInventoryItems: PropTypes.arrayOf(
    PropTypes.shape({
      ndc: PropTypes.string,
      fulfillingPharmacyId: PropTypes.string,
      drugName: PropTypes.string,
      strength: PropTypes.string,
    })
  ),
};

BulkLabelsTable.defaultProps = {
  staleLineItems: new Set(),
  lockedInventoryItems: [],
};

export default BulkLabelsTable;
